import { useCallback, useMemo, useRef, useState } from 'react';
import { useTheme } from '../../../../utils/context/ThemeContext';

// Shared between ShowEmail.tsx (Gmail thread messages) and ShowInboxMessage.tsx (personal SMTP/POP3 inbox) --
// previously each had its own byte-for-byte-near-identical copy of this height-measuring sandboxed iframe.
export const EMAIL_IFRAME_MIN_HEIGHT = 120;
export const HTML_PLACEHOLDER = 'No body found.';

export function getEmailDisplayContent(message: { body?: string | null; htmlBody?: string | null }): string {
  const htmlBody = message?.htmlBody?.trim();
  const body = message?.body?.trim() ?? '';

  if (htmlBody && htmlBody !== HTML_PLACEHOLDER && htmlBody.length > 0) {
    return htmlBody;
  }

  if (!body) return '';

  const htmlStart = body.search(/<\s*!?\s*DOCTYPE\s+html\s*>|<\s*html\s/i);
  if (htmlStart !== -1) {
    return body.slice(htmlStart);
  }

  return body;
}

export function isFullHtmlDocument(content: string): boolean {
  return /^\s*<\s*!?\s*DOCTYPE\s+html\s*>|^\s*<\s*html\s/i.test(content?.trim() ?? '');
}

// The iframe is its own document, isolated from the app's [data-theme] CSS cascade -- its CSS custom
// properties (e.g. var(--bg-secondary)) don't resolve inside it, so the two themes' actual values from
// index.css's :root / [data-theme="dark"] blocks are inlined literally here instead.
const LIGHT_BODY_BG = '#ffffff';
const LIGHT_BODY_TEXT = '#111928';
const DARK_BODY_BG = '#1f2937';
const DARK_BODY_TEXT = '#f9fafb';

// `<base target="_blank">` sends every link in the email body to a new tab without needing inline script --
// safe under the `sandbox` value below, which omits `allow-scripts` (email HTML is untrusted content).
export const WRAPPED_TEMPLATE = (inner: string, isDarkMode: boolean = false) =>
  `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"><title>Email Content</title>
    <style>body{margin:0;padding:0;background:${isDarkMode ? DARK_BODY_BG : LIGHT_BODY_BG};color:${isDarkMode ? DARK_BODY_TEXT : LIGHT_BODY_TEXT};}.bodyclass{}
    /* contentEditable represents an empty line as <div><br></div> -- with no explicit height, a block div
       whose only child is a <br> can collapse to zero height in some engines, silently losing a blank
       line's worth of vertical space when the composed message is reopened for reading. */
    .bodyclass div{min-height:1em;}</style>
    </head><body class="bodyclass">${inner}</body></html>`;

export const EmailBodyIframe = ({ body, onHeightChange }: { body: string; onHeightChange?: () => void }) => {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(`${EMAIL_IFRAME_MIN_HEIGHT}px`);
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const onLoad = useCallback(() => {
    try {
      if (ref.current?.contentWindow?.document?.body) {
        const newHeight = ref.current.contentWindow.document.body.scrollHeight;
        // Adding 20px to prevent vertical scrollbar when horizontal scrollbar is visible
        setHeight(`${Math.max(newHeight, EMAIL_IFRAME_MIN_HEIGHT) + 20}px`);
      }
    } catch (error) {
      console.error('Failed to measure email iframe content height:', error);
      setHeight(`${EMAIL_IFRAME_MIN_HEIGHT}px`);
    } finally {
      // Notify the parent precisely when this message's content has finished measuring, so a caller that
      // needs to re-scroll once layout has settled (e.g. a reply editor positioned below it) doesn't have to
      // guess with a broad DOM observer over unrelated, still-editable siblings (bug/email-date-display).
      onHeightChange?.();
    }
  }, [onHeightChange]);

  const srcDoc = useMemo(() => {
    const content = body ?? '';
    // A full HTML document supplies its own styling (e.g. a marketing email's own light-themed <body>) --
    // only the wrapper template's default body colors are theme-aware, matching how the rest of the app
    // only themes its own chrome, never third-party HTML content.
    if (isFullHtmlDocument(content)) return content;
    return WRAPPED_TEMPLATE(content, isDarkMode);
  }, [body, isDarkMode]);

  return (
    <div className="email-iframe-wrapper">
      <iframe
        ref={ref}
        onLoad={onLoad}
        title="Email content"
        width="100%"
        height={height}
        scrolling="auto"
        frameBorder={0}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        style={{ minHeight: EMAIL_IFRAME_MIN_HEIGHT, width: '100%', display: 'block' }}
        srcDoc={srcDoc}
      />
    </div>
  );
};
